import React, { useEffect, useMemo } from 'react';
import { Formik } from 'formik';
import { LenderClient } from './LenderClient';
import { eApplicationActions } from 'state/orderOverview/actions';
import { useApplicationContext } from 'state/orderOverview/context';
import { LenderClientSchema } from './LenderClientSchema';
import { getLenderUserListAsyncAction } from '../../../../../state/orderOverview/actions/getLenderUserListAsyncAction';
import { useSelector } from 'react-redux';
import { RootStore } from 'store/types';
import { useAppDispatch } from 'store/store';
import { executePartiesActions } from 'store/order/orderData/partiesActionsSlice';
import { getAllClientsAsyncAction } from 'store/clients/clientsSlice';
import { getAllLendersAsyncAction } from 'store/lenders/lendersSlice';
import { loadOrderAsyncAction } from 'store/order/batchedDispatches';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { ILenderCompanyData } from 'types/lenders/ILenderCompany';
import { useGetFilteredLenderClientParties } from './hooks/useGetFilteredLenderClientParties';
import { useUpdateOrderFromSummaryItem } from '../hooks/useUpdateOrderFromSummaryItem';
import { doGetAsyncAssociatedLenders } from '../../../../../ui-toolkit/associatedLenders/dispatch';
import { useAssociatedLendersContext } from '../../../../../ui-toolkit/associatedLenders/AssociatedLendersProvider';
import { ePartyPurpose } from '../../../../../ui-toolkit/enums/ePartyPurpose';
import { IClientData } from '../../../../../ui-toolkit/orders/interfaces/IClient';
import { FullNameOfPerson } from '../../../../../ui-toolkit/orders/interfaces/IParty';
import { IPartyPerson } from '../../../../../ui-toolkit/orders/interfaces/IPartyPerson';
import { isNil } from 'lodash';

export const LenderClientContainer: React.FC = () => {
  const parties = useGetFilteredLenderClientParties();
  const [{ associatedLenders, associatedLendersFetchStatus }, associatedLendersDispatch] =
    useAssociatedLendersContext();
  const { clients, lenders } = useSelector((state: RootStore) => state.usersCollection);
  const reduxDispatch = useAppDispatch();
  const [context, dispatch] = useApplicationContext();
  const { data: orderData } = useSelectCurrentOrder();
  const orderContact = parties.find((party) => party.purpose === ePartyPurpose.order_contact);
  const lender = parties.find((party) => party.purpose === ePartyPurpose.lender);
  const lenderUserList = useMemo(() => (context.lenderUsers ? context.lenderUsers.list : []), [context.lenderUsers]);
  const usersOptions = useMemo(() => {
    return [
      ...(lenderUserList
        ? lenderUserList.map((item) => ({ label: `${FullNameOfPerson(item)} (${item.email})`, value: item.id }))
        : []),
    ].sort((a, b) => (a.label < b.label ? -1 : 1));
  }, [lenderUserList]);

  const { onPatchOrderInformation } = useUpdateOrderFromSummaryItem();

  useEffect(() => {
    reduxDispatch(getAllClientsAsyncAction());
    reduxDispatch(getAllLendersAsyncAction());
  }, [reduxDispatch]);

  useEffect(() => {
    if (orderContact && lender) {
      getLenderUserListAsyncAction(lender.id).then((t) => dispatch(t));
    }
  }, [dispatch, orderContact, lender]);

  const getPartyIdByPurpose = (purpose: ePartyPurpose): string | null => {
    const party = parties.find((party) => party.purpose === purpose);
    if (party) {
      if (purpose === ePartyPurpose.order_contact && (party as IPartyPerson).lender_user_id) {
        return (party as IPartyPerson).lender_user_id!;
      } else {
        return party.id;
      }
    } else return null;
  };

  useEffect(() => {
    const client = parties.find((party) => party.purpose === ePartyPurpose.client);
    if (client && associatedLenders[client.id] === undefined && !associatedLendersFetchStatus.fetching) {
      doGetAsyncAssociatedLenders(client.id, associatedLendersDispatch);
    }
  }, [parties, associatedLenders, associatedLendersDispatch, associatedLendersFetchStatus]);

  const getLenderUsers = async (lenderId: string) => {
    dispatch(await getLenderUserListAsyncAction(lenderId));
  };

  return (
    <Formik
      initialValues={{
        clientId: getPartyIdByPurpose(ePartyPurpose.client),
        lenderId: getPartyIdByPurpose(ePartyPurpose.lender),
        orderContactId: getPartyIdByPurpose(ePartyPurpose.order_contact),
      }}
      validationSchema={LenderClientSchema(
        lenders.data.map((t: ILenderCompanyData) => ({ label: t.name! || '', value: t.id! || '' })),
        clients.data.map((t: IClientData) => ({ label: t.name!, value: t.id! })),
        usersOptions
      )}
      onSubmit={async (values) => {
        dispatch({
          type: eApplicationActions.ChangePartyModalMessage,
          payload: { verb: 'updated', updating: true, updated: false, error: '', hasExtraSuccessWarning: false },
        });
        let count = 0;
        const newLender = lenders.data.find((lender: ILenderCompanyData) => lender.id === values.lenderId);
        const newClient = clients.data.find((client: IClientData) => client.id === values.clientId);
        let newData: Record<string, any> = {};

        if (values.lenderId !== getPartyIdByPurpose(ePartyPurpose.lender) && newLender) {
          newData.lenderCompanyId = values.lenderId;
          count++;
        }

        if (values.clientId !== getPartyIdByPurpose(ePartyPurpose.client) && newClient) {
          newData.clientCompanyId = values.clientId;
          count++;
        }

        try {
          await onPatchOrderInformation(newData);
          if (!isNil(values.orderContactId) && orderContact && values.orderContactId !== orderContact.id) {
            // call edit party action if order contact was changed
            const lenderUser = lenderUserList ? lenderUserList.find((user) => user.id === values.orderContactId) : null;
            if (lenderUser) {
              await reduxDispatch(
                executePartiesActions({
                  orderId: orderData.transaction.id!,
                  parties: {
                    deleteParties: [],
                    createParties: [],
                    updateParties: [
                      {
                        ...orderContact,
                        lender_user_id: values.orderContactId!,
                        first_name: lenderUser.first_name,
                        last_name: lenderUser.last_name,
                      } as IPartyPerson,
                    ],
                  },
                })
              );
            }
            count++;
          } else if (!values.orderContactId && orderContact) {
            count++;
            await reduxDispatch(
              executePartiesActions({
                orderId: orderData.transaction.id!,
                parties: { createParties: [], updateParties: [], deleteParties: [orderContact] },
              })
            );
          }
          reduxDispatch(loadOrderAsyncAction(orderData.transaction.id!));
          dispatch({
            type: eApplicationActions.ChangePartyModalMessage,
            payload: {
              verb: 'updated',
              multipleThingsHaveBeenUpdated: count > 1,
              updating: false,
              updated: true,
              error: '',
              hasExtraSuccessWarning: false,
            },
          });
        } catch (error: any) {
          dispatch({
            type: eApplicationActions.ChangePartyModalMessage,
            payload: { verb: 'updated', updating: false, updated: true, error, hasExtraSuccessWarning: false },
          });
        }
      }}
    >
      <LenderClient
        parties={parties}
        associatedLenders={associatedLenders}
        allClients={clients.data.map((t: IClientData) => ({ label: t.name, value: t.id }))}
        allUsers={usersOptions}
        getAssociatedLenders={(clientId: string) => doGetAsyncAssociatedLenders(clientId, associatedLendersDispatch)}
        getLenderUsers={getLenderUsers}
      />
    </Formik>
  );
};
